import {
  accessories,
  getColorById,
  hardware,
  keralitProfileSystems,
  isKeralitProfileSystem,
  normalizeKeralitProfileSystem,
  plastisolProfileSystems,
  isPlastisolProfileSystem,
  normalizePlastisolProfileSystem,
} from '../data/mockData';
import {
  calcVoordeurWoodJointPremium,
  getConfiguratorGlazingAreaSqm,
  getPlastisolMeters,
  countDoorVentilators,
  countKozijnenOpeningMechanismParts,
  countKozijnenOverheadOpeningMechanismParts,
  countKozijnenOverheadParts,
  countKozijnenUnderlightParts,
  getSchuifpuiActiveVentilatorKeys,
  isKozijnenOpeningWindowType,
} from './pdfPricing';
import { getVmkGlazingLabel, getVmkSandwichLabel, VMK_GLAZING_LABEL } from './vmkGlazing';
import { getFilledAdditionalItems } from './additionalItems';

const FIXED_FRAME_OFFSET_MM = 140;
const MULTI_PART_GLASS_WIDTH_OFFSET_MM = 96;


export const VMK_PROFILE_NAME = 'Blokkader 15 graden';
export const VMK_DOOR_SUBTITLE = 'BUVA VERDIEPT 55 - scharnier: GEEN RAL KLEUR';
export { VMK_GLAZING_LABEL } from './vmkGlazing';

export const getLetterLabel = (index) => String.fromCharCode(65 + index);

export const detectPageType = (config) => {
  if (!config) return 'kozijnen';

  // Prefer explicit productType stamped by configurators / saved quotes
  const explicit = String(config.productType || '').trim().toLowerCase();
  const KNOWN = new Set([
    'dakkapel',
    'plastisol',
    'plastisol-zetwerk',
    'keralit',
    'voordeur',
    'achterdeur',
    'terrasdeur',
    'schuifpui',
    'kozijnen',
  ]);
  if (KNOWN.has(explicit)) {
    return explicit === 'plastisol-zetwerk' ? 'plastisol' : explicit;
  }

  if (config.productType === 'dakkapel') return 'dakkapel';
  // Plastisol / Keralit before door heuristics — cladding configs must not fall through to kozijnen
  if (isPlastisolProfileSystem(config.profileSystem) || config.productType === 'plastisol') {
    return 'plastisol';
  }
  if (
    isKeralitProfileSystem(config.profileSystem) ||
    (config.lineSpacing !== undefined &&
      config.lineType !== undefined &&
      config.parts === undefined &&
      config.doorStyle === undefined &&
      config.designType === undefined &&
      config.slidingPosition === undefined &&
      config.topLight === undefined &&
      config.numberOfParts === undefined)
  ) {
    return 'keralit';
  }
  // Door heuristics — order matters (achterdeur has topLight + designType)
  if (config.doorStyle !== undefined) return 'voordeur';
  if (config.topLight !== undefined || config.numberOfParts !== undefined) return 'achterdeur';
  if (config.designType !== undefined) return 'terrasdeur';
  if (config.doorType === 'sliding' || config.slidingPosition) return 'schuifpui';
  return 'kozijnen';
};

/** Resolve page/product type preferring the saved quote type, then config.productType. */
export const resolveQuotePageType = (config, quoteType) => {
  const fromQuote = String(quoteType || '').trim().toLowerCase();
  if (
    fromQuote &&
    [
      'dakkapel',
      'plastisol',
      'keralit',
      'voordeur',
      'achterdeur',
      'terrasdeur',
      'schuifpui',
      'kozijnen',
    ].includes(fromQuote)
  ) {
    return fromQuote;
  }
  return detectPageType(config);
};

/**
 * VMK catalog leaf/sash line — must match the configured product (not always Voordeur).
 * Codes from VMK PDF extracts: 9433 voordeur, 9453 tuindeuren/achterdeuren.
 */
export const getDoorLeafDescription = (pageType) => {
  switch (pageType) {
    case 'achterdeur':
      return '9453 Achterdeur vleugel';
    case 'terrasdeur':
      return '9453 Terrasdeur vleugel';
    case 'schuifpui':
      return '9453 Schuifpui vleugel';
    case 'voordeur':
    default:
      return '9433 Voordeur vleugel';
  }
};

export const isKeralitQuote = (quoteOrConfig, quoteType) => {
  if (quoteType === 'keralit') return true;
  const config = quoteOrConfig?.config || quoteOrConfig;
  if (!config) return false;
  if (isKeralitProfileSystem(config.profileSystem)) return true;
  // Legacy / type-missing Keralit saves: cladding flags without window/door shape fields
  return (
    config.lineSpacing !== undefined &&
    config.lineType !== undefined &&
    config.parts === undefined &&
    config.doorStyle === undefined &&
    config.designType === undefined &&
    config.slidingPosition === undefined &&
    config.topLight === undefined &&
    config.numberOfParts === undefined &&
    !isPlastisolProfileSystem(config.profileSystem) &&
    config.productType !== 'plastisol'
  );
};

export const isPlastisolQuote = (quoteOrConfig, quoteType) => {
  if (quoteType === 'plastisol') return true;
  const config = quoteOrConfig?.config || quoteOrConfig;
  if (!config) return false;
  return (
    isPlastisolProfileSystem(config.profileSystem) ||
    config.productType === 'plastisol' ||
    config.productType === 'plastisol-zetwerk'
  );
};

export const isDakkapelQuote = (quoteOrConfig, quoteType) => {
  if (quoteType === 'dakkapel') return true;
  const config = quoteOrConfig?.config || quoteOrConfig;
  if (!config) return false;
  return config.productType === 'dakkapel';
};

const DOOR_PAGE_TYPES = new Set(['voordeur', 'achterdeur', 'terrasdeur', 'schuifpui']);

/** Which product families appear in drawings (windows / doors / Keralit / Plastisol). */
export const getQuoteProductFlags = (savedQuotes = []) => {
  let hasWindows = false;
  let hasDoors = false;
  let hasKeralit = false;
  let hasPlastisol = false;

  (savedQuotes || []).forEach((quote) => {
    const type = quote?.type || detectPageType(quote?.config || {});
    if (type === 'plastisol' || isPlastisolQuote(quote, type)) {
      hasPlastisol = true;
      return;
    }
    if (type === 'keralit' || isKeralitQuote(quote, type)) {
      hasKeralit = true;
      return;
    }
    if (DOOR_PAGE_TYPES.has(type)) {
      hasDoors = true;
      return;
    }
    hasWindows = true;
  });

  return { hasWindows, hasDoors, hasKeralit, hasPlastisol };
};

/**
 * Investment table product line, e.g. "Kunststof kozijnen, deuren & Keralit".
 * Falls back to the classic frames+doors label when nothing is detected.
 */
export const getInvestmentProductLabel = (savedQuotes, t) => {
  const { hasWindows, hasDoors, hasKeralit, hasPlastisol } = getQuoteProductFlags(savedQuotes);
  if (!hasWindows && !hasDoors && !hasKeralit && !hasPlastisol) {
    return t('quote.template.rowFrames');
  }

  const prefix = t('quote.template.rowProductPrefix');
  const windows = t('quote.template.rowProductWindows');
  const doors = t('quote.template.rowProductDoors');
  const keralit = t('quote.template.rowProductKeralit');
  const plastisol = t('quote.template.rowProductPlastisol');
  const and = t('quote.template.rowProductAnd');

  const plasticParts = [];
  if (hasWindows) plasticParts.push(windows);
  if (hasDoors) plasticParts.push(doors);

  const claddingParts = [];
  if (hasKeralit) claddingParts.push(keralit);
  if (hasPlastisol) claddingParts.push(plastisol);

  if (plasticParts.length === 0) {
    if (claddingParts.length === 1) return claddingParts[0];
    if (claddingParts.length === 2) return `${claddingParts[0]} ${and} ${claddingParts[1]}`;
    return keralit;
  }

  let plasticLabel;
  if (plasticParts.length === 1) {
    plasticLabel = `${prefix} ${plasticParts[0]}`;
  } else {
    plasticLabel = `${prefix} ${plasticParts[0]} ${and} ${plasticParts[1]}`;
  }

  if (claddingParts.length === 0) {
    return plasticLabel;
  }

  if (plasticParts.length === 2 && claddingParts.length === 1) {
    return `${prefix} ${plasticParts[0]}, ${plasticParts[1]} ${and} ${claddingParts[0]}`;
  }

  const claddingLabel =
    claddingParts.length === 1
      ? claddingParts[0]
      : `${claddingParts[0]} ${and} ${claddingParts[1]}`;
  return `${plasticLabel} ${and} ${claddingLabel}`;
};

/** True when saved/export SVG is the Keralit profile logo (even if quote.type was lost). */
export const svgLooksLikeKeralit = (svgHtml) => {
  if (!svgHtml || typeof svgHtml !== 'string') return false;
  return /keralit|aria-label="Keralit/i.test(svgHtml);
};

export const svgLooksLikePlastisol = (svgHtml) => {
  if (!svgHtml || typeof svgHtml !== 'string') return false;
  return /plastisol|aria-label="Plastisol/i.test(svgHtml);
};

export const svgLooksLikeDakkapel = (svgHtml) => {
  if (!svgHtml || typeof svgHtml !== 'string') return false;
  return /dakkapel|aria-label="Dakkapel/i.test(svgHtml);
};

/** Windows/doors only — never Keralit/Plastisol/Dakkapel cladding. */
export const shouldShowNijBegunMaatregelcode = (quote, svgHtml = '') => {
  if (isPlastisolQuote(quote, quote?.type)) return false;
  if (isKeralitQuote(quote, quote?.type)) return false;
  if (isDakkapelQuote(quote, quote?.type)) return false;
  if (svgLooksLikePlastisol(svgHtml)) return false;
  if (svgLooksLikeKeralit(svgHtml)) return false;
  if (svgLooksLikeDakkapel(svgHtml)) return false;
  const type = quote?.type || detectPageType(quote?.config || {});
  return (
    type === 'kozijnen' ||
    type === 'voordeur' ||
    type === 'achterdeur' ||
    type === 'terrasdeur' ||
    type === 'schuifpui'
  );
};

export const getKeralitProfileName = (profileSystemId) => {
  const normalized = normalizeKeralitProfileSystem(profileSystemId);
  const profile = keralitProfileSystems.find((item) => item.id === normalized);
  return profile?.name || keralitProfileSystems[0]?.name || '190';
};

export const getPlastisolProfileName = (profileSystemId) => {
  const normalized = normalizePlastisolProfileSystem(profileSystemId);
  const profile = plastisolProfileSystems.find((item) => item.id === normalized);
  return profile?.name || plastisolProfileSystems[0]?.name || 'Plastisol Zetwerk';
};

const getPartWidths = (config, parts, totalWidth) => {
  if (Array.isArray(config.partWidths) && config.partWidths.length === parts) {
    const sum = config.partWidths.reduce((acc, value) => acc + Number(value), 0);
    if (sum > 0) return config.partWidths.map((value) => Number(value));
  }
  const base = Math.floor(totalWidth / parts);
  const widths = Array(parts).fill(base);
  widths[parts - 1] += totalWidth - base * parts;
  return widths;
};

export const formatMm = (value) => `${Math.round(value)}mm`;

/** Parse a dimension string like "1360mm" to millimeters. */
export const parseMmDimension = (value) => {
  const match = String(value || '').match(/^(\d+(?:[.,]\d+)?)\s*mm$/i);
  if (!match) return null;
  return Number(String(match[1]).replace(',', '.'));
};

/** Format a linear-meter length for VMK quote tables, e.g. "2,25 m". */
export const formatMeters = (meters) => {
  if (!Number.isFinite(meters) || meters <= 0) return '';
  const formatted = meters.toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} m`;
};

/** Format a square-meter area for VMK quote tables, e.g. "1,85 m²". */
export const formatSqm = (areaSqm) => {
  if (!Number.isFinite(areaSqm) || areaSqm <= 0) return '';
  const formatted = areaSqm.toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} m²`;
};

/** Convert width × height mm strings to a single m² display value. */
export const sqmDisplayFromMmDims = (dim1, dim2) => {
  const w = parseMmDimension(dim1);
  const h = parseMmDimension(dim2);
  if (w == null || h == null || w <= 0 || h <= 0) {
    return { dim1: dim1 || '', dim2: dim2 || '' };
  }
  return { dim1: formatSqm((w * h) / 1_000_000), dim2: '' };
};

/** Single drawing area in m² (quantity included). */
export const getQuoteAreaSqm = (quote) => {
  const quantity = quote?.config?.quantity || 1;
  let area = parseFloat(quote?.dimensions?.area);
  if (!Number.isFinite(area) || area <= 0) {
    const w = Number(quote?.config?.width ?? quote?.dimensions?.width);
    const h = Number(quote?.config?.height ?? quote?.dimensions?.height);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      area = (w * h) / 1_000_000;
    } else {
      area = 0;
    }
  }
  return area * quantity;
};

/**
 * Sum of drawing areas in m² for export totals.
 * Keralit SQM is only counted when every drawing in the list is Keralit;
 * when mixed with Kozijnen/doors/etc., Keralit cladding area is excluded
 * so it does not inflate "Totaal oppervlakte".
 */
export const getTotalQuotesAreaSqm = (quotes = []) => {
  const list = Array.isArray(quotes) ? quotes : [];
  if (list.length === 0) return 0;

  const hasNonKeralit = list.some(
    (quote) => !isKeralitQuote(quote, quote?.type),
  );

  return list.reduce((sum, quote) => {
    if (hasNonKeralit && isKeralitQuote(quote, quote?.type)) {
      return sum;
    }
    return sum + getQuoteAreaSqm(quote);
  }, 0);
};

const VMK_DUTCH_COLOR_NAMES = {
  'Light Ivory': 'Licht ivoor',
  'Wine Red': 'Wijnrood',
  Red: 'Rood',
  'Ultramarine Blue': 'Ultramarine blauw',
  'Monument Blue': 'Monument blauw',
  'Steel Blue': 'Staalblauw',
  'Cobalt Blue': 'Kobaltblauw',
  'Brilliant Blue': 'Brilliant blauw',
  'Fir Green': 'Dennegroen',
  'Moss Green': 'Mosgroen',
  'Monument Green': 'Monument groen',
  'Silver Grey': 'Zilvergrijs',
  'Signal Grey': 'Signaalgrijs',
  'Basalt Grey': 'Basaltgrijs',
  'Slate Grey': 'Leigrijs',
  Anthracite: 'Antraciet',
  'Smooth Anthracite': 'Antraciet',
  'Black Grey': 'Zwartgrijs',
  'Stone Grey': 'Steengrijs',
  'Light Grey': 'Lichtgrijs',
  'Quartz Grey': 'Kwartsgrijs',
  'Silk Grey': 'Zijdegrijs',
  'Golden Oak': 'Goud eiken',
  'Black Brown': 'Zwartbruin',
  Cream: 'Crème',
  'Black Woodgrain Ligna': 'Zwart Ligna',
  White: 'Reinwit',
  'White Smooth': 'Wit glad',
};

const toVmkDutchColorName = (colorData, fallbackRal) => {
  if (!colorData?.name) return fallbackRal ? `RAL ${fallbackRal}` : '';
  return VMK_DUTCH_COLOR_NAMES[colorData.name] || colorData.name;
};

const formatVmkExteriorColor = (colorId) => {
  const color = colorId ? getColorById(colorId) : null;
  const ral = color?.ral || '9010';
  const name = toVmkDutchColorName(color, ral);

  if (ral === '7016') return `Antraciet houtnerf buiten ${ral}`;
  if (ral === '9010') return `Reinwit houtnerf ${ral} buiten`;
  return `${name} houtnerf ${ral} buiten`;
};

const formatVmkDraaideelColor = (colorId) => {
  const color = colorId ? getColorById(colorId) : null;
  const ral = color?.ral || '7016';
  const name = toVmkDutchColorName(color, ral);

  if (ral === '7016') return `Antraciet houtnerf draaideel ${ral}`;
  if (ral === '9010') return `Reinwit houtnerf ${ral} draaideel`;
  if (ral === '9016') return `Wit glad draaideel ${ral}`;
  return `${name} houtnerf ${ral} draaideel`;
};

const formatVmkInteriorColor = (colorId) => {
  const color = colorId ? getColorById(colorId) : null;
  let ral = color?.ral || '9016';
  if (colorId === 'white' || colorId === 'ral-9010') {
    ral = '9016';
  }
  return `Binnen glad ${ral}`;
};

/** Opening / turning sash types that use a "draaideel" color on the VMK write-up. */
const OPENING_DRAAIDEEL_WINDOW_TYPES = new Set([
  'tilt-turn-left',
  'tilt-turn-right',
  'tilt-only',
]);

const pushWindowTypeIds = (target, value) => {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach((entry) => pushWindowTypeIds(target, entry));
    return;
  }
  const id = String(value).trim();
  if (id) target.push(id);
};

/** Collect raamtypes from main parts + overhead/underlight (incl. section splits). */
export const collectKozijnenWindowTypeIds = (config) => {
  if (!config) return [];
  const types = [];
  const parts = Math.max(1, Number(config.parts) || 1);

  for (let i = 0; i < parts; i += 1) {
    pushWindowTypeIds(
      types,
      config.partWindowTypes?.[i] ||
        (parts === 1 ? config.raamtype || config.windowType?.id : null) ||
        'fixed-sash'
    );
  }

  pushWindowTypeIds(types, config.overheadPartWindowTypes);
  pushWindowTypeIds(types, config.partOverheadWindowTypes);
  pushWindowTypeIds(types, config.partOverheadSectionWindowTypes);
  pushWindowTypeIds(types, config.underlightPartWindowTypes);
  pushWindowTypeIds(types, config.partUnderlightWindowTypes);
  pushWindowTypeIds(types, config.partUnderlightSectionWindowTypes);

  return types;
};

/**
 * True when the drawing has an openable sash that should be labelled "draaideel".
 * Kozijnen that are panels / fixed only must not show draaideel on the color line.
 * Doors and sliding systems always have an opening leaf.
 */
export const configHasDraaideelColor = (config, pageType = detectPageType(config)) => {
  if (
    pageType === 'voordeur' ||
    pageType === 'achterdeur' ||
    pageType === 'terrasdeur' ||
    pageType === 'schuifpui'
  ) {
    return true;
  }
  if (pageType === 'keralit') {
    return false;
  }
  if (pageType === 'plastisol') {
    return false;
  }
  return collectKozijnenWindowTypeIds(config).some((typeId) =>
    OPENING_DRAAIDEEL_WINDOW_TYPES.has(typeId)
  );
};

/**
 * Buitenvlakkleur - (Kleur draaideel) - Binnenvlakkleur.
 * Omit the draaideel segment for panel/fixed-only kozijnen.
 */
export const formatVmkColorLine = (
  exteriorColorId,
  draaideelColorId,
  interiorColorId,
  { includeDraaideel = true } = {}
) => {
  const exterior = formatVmkExteriorColor(exteriorColorId);
  const interior = formatVmkInteriorColor(interiorColorId);
  if (!includeDraaideel) {
    return `(${exterior} - ${interior})`;
  }
  return `(${exterior} - ${formatVmkDraaideelColor(draaideelColorId)} - ${interior})`;
};

const getRalLabel = (colorId, fallbackRal = '7016') => {
  const colorData = getColorById(colorId);
  return colorData?.ral ? `RAL ${colorData.ral.replace(/^0+/, '')}` : `RAL ${fallbackRal}`;
};

/** VMK table labels: match configurator UI (Verstek / HVL). */
export const getWoodJointLabel = (woodJointId) => {
  if (woodJointId === 'joint-90') return 'HVL';
  if (woodJointId === 'joint-45') return 'Verstek';
  return woodJointId ? String(woodJointId) : 'Verstek';
};

/** Resolve selected verbinding; default Verstek when missing on older drawings. */
export const resolveWoodJointId = (config) => {
  if (config?.woodJoint === 'joint-90' || config?.hvlSurcharge === true) return 'joint-90';
  if (config?.woodJoint === 'joint-45') return 'joint-45';
  return 'joint-45';
};

const buildVerbindingRow = (config) => ({
  description: getWoodJointLabel(resolveWoodJointId(config)),
});

const OPENING_TYPE_LABELS_NL = {
  'tilt-turn-left': 'Kiep-Draai Links',
  'tilt-turn-right': 'Kiep-Draai Rechts',
  'tilt-only': 'Alleen Kiep',
};

const countTruthyFlags = (flags) =>
  Array.isArray(flags) ? flags.filter(Boolean).length : 0;

/** Active ventilatie-rooster count across Kozijnen main / bovenlicht / onderlicht. */
const countKozijnenVentilators = (config) =>
  countTruthyFlags(config?.ventilatorParts) +
  countTruthyFlags(config?.ventilatorOverheadParts) +
  countTruthyFlags(config?.ventilatorUnderlightParts);

/** Collect opening-type labels (with counts) for main + overhead sashes. */
const collectOpeningTypeSummary = (config) => {
  const counts = new Map();
  const bump = (type) => {
    if (!isKozijnenOpeningWindowType(type)) return;
    counts.set(type, (counts.get(type) || 0) + 1);
  };

  const parts = Number(config?.parts) || 1;
  const mainTypes = config?.partWindowTypes || [];
  for (let i = 0; i < parts; i += 1) {
    bump(mainTypes[i] ?? mainTypes[0] ?? 'fixed-sash');
  }

  if (config?.overhead === 'overhead') {
    const overheadParts = Math.max(1, Number(config.overheadParts) || 1);
    const types = config.overheadPartWindowTypes || [];
    for (let i = 0; i < overheadParts; i += 1) {
      bump(types[i] ?? types[0] ?? 'fixed-sash');
    }
  } else if (Array.isArray(config?.overheadPerPart)) {
    const sectionRows = config.partOverheadSectionWindowTypes || [];
    config.overheadPerPart.forEach((enabled, i) => {
      if (!enabled) return;
      const sectionCount = Math.max(1, Math.round(Number(config.partOverheadSections?.[i]) || 1));
      const row = sectionRows[i];
      const legacy = config.partOverheadWindowTypes?.[i] ?? 'fixed-sash';
      for (let s = 0; s < sectionCount; s += 1) {
        bump(row?.[s] ?? legacy);
      }
    });
  }

  if (config?.underlight === 'underlight') {
    const underlightParts = Math.max(1, Number(config.underlightParts) || 1);
    const types = config.underlightPartWindowTypes || [];
    for (let i = 0; i < underlightParts; i += 1) {
      bump(types[i] ?? types[0] ?? 'fixed-sash');
    }
  } else if (Array.isArray(config?.underlightPerPart)) {
    const sectionRows = config.partUnderlightSectionWindowTypes || [];
    config.underlightPerPart.forEach((enabled, i) => {
      if (!enabled) return;
      const sectionCount = Math.max(1, Math.round(Number(config.partUnderlightSections?.[i]) || 1));
      const row = sectionRows[i];
      const legacy = config.partUnderlightWindowTypes?.[i] ?? 'fixed-sash';
      for (let s = 0; s < sectionCount; s += 1) {
        bump(row?.[s] ?? legacy);
      }
    });
  }

  return [...counts.entries()].map(([type, count]) => {
    const label = OPENING_TYPE_LABELS_NL[type] || type;
    return count > 1 ? `${label} (×${count})` : label;
  });
};

const appendSelectedOptionRows = (rows, config, pageType) => {
  const addRow = (description, subline) => {
    if (!description) return;
    rows.push({ description, ...(subline ? { subline } : {}) });
  };

  // Verbinding is added separately (early in the table) so it is always visible.

  // Ventilatie rooster — Kozijnen / doors / schuifpui / dakkapel
  if (pageType === 'kozijnen') {
    const ventilatorCount = countKozijnenVentilators(config);
    if (ventilatorCount > 0) {
      addRow(
        'Ventilatie rooster',
        ventilatorCount > 1 ? `×${ventilatorCount}` : null,
      );
    }
  } else if (
    pageType === 'voordeur' ||
    pageType === 'achterdeur' ||
    pageType === 'terrasdeur'
  ) {
    const doorVentCount = countDoorVentilators(config);
    if (doorVentCount > 0) {
      addRow(
        'Ventilatie rooster',
        doorVentCount > 1 ? `×${doorVentCount}` : null,
      );
    }
  } else if (pageType === 'schuifpui') {
    const slidingVentCount = getSchuifpuiActiveVentilatorKeys(config).length;
    if (slidingVentCount > 0) {
      addRow(
        'Ventilatie rooster',
        slidingVentCount > 1 ? `×${slidingVentCount}` : null,
      );
    }
  } else if (pageType === 'dakkapel') {
    const grilleCount = Math.max(0, Number(config.ventilationGrilles) || 0);
    if (grilleCount > 0) {
      addRow(
        'Ventilatierooster',
        grilleCount > 1 ? `×${grilleCount}` : null,
      );
    }
  }

  // Bovenlicht / onderlicht (structure) — so the quote matches the drawing
  if (pageType === 'kozijnen') {
    const overheadParts = countKozijnenOverheadParts(config);
    if (overheadParts > 0) {
      const heightMm = Number(config.overheadHeight) || 300;
      const bits = [`${Math.round(heightMm)} mm`];
      if (overheadParts > 1) bits.push(`${overheadParts} onderdelen`);
      addRow('Bovenlicht', bits.join(' — '));
    }

    const underlightParts = countKozijnenUnderlightParts(config);
    if (underlightParts > 0) {
      const heightMm = Number(config.underlightHeight) || 300;
      const bits = [`${Math.round(heightMm)} mm`];
      if (underlightParts > 1) bits.push(`${underlightParts} onderdelen`);
      addRow('Onderlicht', bits.join(' — '));
    }

    const openingSummary = collectOpeningTypeSummary(config);
    if (openingSummary.length > 0) {
      addRow('Openmechanisme', openingSummary.join(', '));
    } else {
      // Fallback counts if types were stored oddly but priced
      const mainOpen = countKozijnenOpeningMechanismParts(config);
      const overheadOpen = countKozijnenOverheadOpeningMechanismParts(config);
      const totalOpen = mainOpen + overheadOpen;
      if (totalOpen > 0) {
        addRow('Openmechanisme', totalOpen > 1 ? `×${totalOpen}` : null);
      }
    }
  }

  if (config.screenElectricallyOperated === true) {
    addRow('Scherm elektrisch bediend', getRalLabel(config.screenColor));
  }

  if (config.rollerShutterElectricallyOperated === true) {
    addRow('Rolluik elektrisch bediend', getRalLabel(config.rollerShutterColor));
  }

  if (config.hor === true) {
    addRow('Hor', getRalLabel(config.horColor));
  }

  if (config.buoySharing === true) {
    const mmText = config.buoySharingMm ? `${config.buoySharingMm}mm` : null;
    const subline = [getRalLabel(config.buoySharingColor), mmText].filter(Boolean).join(' - ');
    addRow('Boeidelen', subline);
  }

  if (Array.isArray(config.hardware)) {
    config.hardware.forEach((hardwareId) => {
      const item = hardware.find((entry) => entry.id === hardwareId);
      addRow('Hardware', item?.name || hardwareId);
    });
  }

  if (Array.isArray(config.accessories)) {
    config.accessories.forEach((accessoryId) => {
      const item = accessories.find((entry) => entry.id === accessoryId);
      addRow('Accessoire', item?.name || accessoryId);
    });
  }

  getFilledAdditionalItems(config.additionalItems).forEach((item) => {
    addRow('Extra optie', (item.name || '').trim() || 'Additional item');
  });

  if (pageType === 'kozijnen' && config.rampDorpel && config.rampDorpel !== 'no-ramp-dorpel') {
    const rampLabels = {
      'ramp-dorpel-30mm': 'Aanslagdorpel 30mm',
      'ramp-dorpel-45mm': 'Aanslagdorpel 45mm',
      'ramp-dorpel-60mm': 'Aanslagdorpel 60mm',
    };
    addRow('Aanslagdorpel', rampLabels[config.rampDorpel] || config.rampDorpel);
  }

  if (pageType === 'kozijnen' && config.vensterbank && config.vensterbank !== 'no-vensterbank') {
    const sillLabels = {
      'vensterbank-30mm': 'Vensterbank 30mm',
    };
    addRow('Vensterbank', sillLabels[config.vensterbank] || config.vensterbank);
  }

  // Dakkapel extras the drawing already includes but the write-up omitted
  if (pageType === 'dakkapel') {
    if (config.sunProtection === 'screens') addRow('Zonwering', 'Screens');
    if (config.sunProtection === 'shutters') addRow('Zonwering', 'Shutters');
    const insect = Math.max(0, Number(config.insectScreens) || 0);
    if (insect > 0) addRow('Insectenhor', insect > 1 ? `×${insect}` : null);
    if (config.permitApplication) addRow('Vergunningaanvraag');
    if (config.debrisRemoval) addRow('Puinafvoer');
    if (config.airco) addRow('Airco');
  }
};

export const getGlassDimensionsForPart = (partWidthMm, heightMm, parts = 1) => {
  const glassW =
    parts === 1
      ? Math.max(0, partWidthMm - FIXED_FRAME_OFFSET_MM)
      : Math.max(0, partWidthMm - MULTI_PART_GLASS_WIDTH_OFFSET_MM);
  const glassH = Math.max(0, heightMm - FIXED_FRAME_OFFSET_MM);
  return { width: glassW, height: glassH };
};

export const getGlassDimensionRows = (config) => {
  const width = config.width || 0;
  const height = config.height || 0;
  const parts = config.parts || 1;
  const partWidths = getPartWidths(config, parts, width);

  return partWidths.map((partWidth) => getGlassDimensionsForPart(partWidth, height, parts));
};

const getColorIds = (config) => {
  const exterior = config.exteriorColor || config.color || 'anthracite';
  const interior = config.interiorFrameColor || config.interiorColor || 'ral-9016';
  const draaideel = config.draaideelColor || exterior || 'anthracite';
  return { exterior, draaideel, interior };
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildWindowSpecRows = (config) => {
  const pageType = detectPageType(config);
  const { exterior, draaideel, interior } = getColorIds(config);
  const colorLine = formatVmkColorLine(exterior, draaideel, interior, {
    includeDraaideel: configHasDraaideelColor(config, pageType),
  });
  const rows = [];

  rows.push({
    description: '9495 Blokkader 15 graden met aanslag met afwerknaad',
    subline: colorLine,
    dim1: '90mm',
    dim2: '120mm',
  });


  rows.push(buildVerbindingRow(config));

  getGlassDimensionRows(config).forEach((dims) => {
    rows.push({
      description: getVmkGlazingLabel(config),
      dim1: formatMm(dims.width),
      dim2: formatMm(dims.height),
    });
  });

  appendSelectedOptionRows(rows, config, pageType);

  return rows;
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildDoorSpecRows = (config, quoteType) => {
  const pageType = resolveQuotePageType(config, quoteType);
  const { exterior, draaideel, interior } = getColorIds(config);
  const colorLine = formatVmkColorLine(exterior, draaideel, interior, {
    includeDraaideel: configHasDraaideelColor(config, pageType),
  });
  const width = config.width || 0;
  const height = config.height || 0;
  const glassDims = getGlassDimensionsForPart(width, height, 1);

  const rows = [
    {
      description: '9491 Blokkader 15 graden met aanslag zonder afwerknaad',
      subline: colorLine,
      dim1: '90mm',
      dim2: '120mm',
    },
    {
      description: 'BUVA Dorpel 55mm',
      dim1: '55mm',
      dim2: '132mm',
    },
    {
      description: getDoorLeafDescription(pageType),
      dim1: '125mm',
      dim2: '82mm',
    },
    {
      description: '9461 T-profiel vlak 82-92',
      dim1: '92mm',
      dim2: '82mm',
    },
    {
      description: getVmkGlazingLabel(config),
      dim1: formatMm(glassDims.width),
      dim2: formatMm(Math.min(glassDims.height, height - 313)),
    },
  ];

  rows.splice(1, 0, buildVerbindingRow(config));

  if (pageType === 'voordeur' && config.doorStyle !== 'style-4') {
    const panelWidth = Math.max(0, Math.round(width * 0.45));
    rows.push({
      description: getVmkSandwichLabel(config),
      dim1: formatMm(panelWidth),
      dim2: formatMm(Math.max(0, height - 313)),
    });
  }

  rows.push(
    { description: 'RVS opdekscharnieren 4x' },
    { description: 'Dom vh dubb.cilinder Plura 333 45-60** kl.1' },
    { description: 'KT Rozet SKG-3' },
    { description: 'Deurgreep stanggreep 800 buiten schuine pootjes' },
    { description: "Extra's" }
  );

  if (config.woodJoint === 'joint-90' || config.hvlSurcharge === true) {
    rows.push({
      description: 'Toeslag HVL',
      unitPrice: calcVoordeurWoodJointPremium(config),
    });
  }

  appendSelectedOptionRows(rows, config, pageType);

  return rows;
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildKeralitSpecRows = (config) => {
  const { exterior, interior } = getColorIds(config);
  const colorLine =
    exterior === interior
      ? `(${formatVmkExteriorColor(exterior)})`
      : formatVmkColorLine(exterior, exterior, interior, { includeDraaideel: false });
  const width = config.width || 0;
  const height = config.height || 0;
  const areaSqm = (width * height) / 1_000_000;

  const rows = [
    {
      description: getKeralitProfileName(config.profileSystem),
      subline: colorLine,
      dim1: formatSqm(areaSqm),
      dim2: '',
    },
  ];

  appendSelectedOptionRows(rows, config, 'keralit');

  return rows;
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildPlastisolSpecRows = (config) => {
  // Plastisol only has buitenvlakkleur — priced per linear meter
  const exterior = config?.exteriorColor || config?.color || 'white';
  const colorLine = `(${formatVmkExteriorColor(exterior)})`;
  const meters = getPlastisolMeters(config);

  const rows = [
    {
      description: getPlastisolProfileName(config.profileSystem),
      subline: colorLine,
      dim1: formatMeters(meters),
      dim2: '',
    },
  ];

  appendSelectedOptionRows(rows, config, 'plastisol');

  return rows;
};

export const buildSpecRows = (config, quoteType) => {
  if (isPlastisolQuote(config, quoteType)) {
    return buildPlastisolSpecRows(config?.config || config);
  }
  if (isKeralitQuote(config, quoteType)) {
    return buildKeralitSpecRows(config?.config || config);
  }
  if (isDakkapelQuote(config, quoteType)) {
    return buildWindowSpecRows(config?.config || config);
  }
  const pageType = resolveQuotePageType(config, quoteType);
  if (
    pageType === 'voordeur' ||
    pageType === 'achterdeur' ||
    pageType === 'terrasdeur'
  ) {
    return buildDoorSpecRows(config, pageType);
  }
  return buildWindowSpecRows(config);
};

/** Total glazing pane area (m²) — matches HR++ rows on the VMK quote table. */
export const getTotalGlazingAreaSqm = (config) =>
  getConfiguratorGlazingAreaSqm(config, detectPageType(config));

export const getItemHeaderSubtitle = (config, quoteType) => {
  const pageType = resolveQuotePageType(config, quoteType);
  if (pageType === 'voordeur' || pageType === 'achterdeur' || pageType === 'terrasdeur') {
    return VMK_DOOR_SUBTITLE;
  }
  return null;
};

export const formatVmkUnitPrice = (unitPrice, quantity, lineTotal, formatPrice) =>
  `${quantity}x  ${formatPrice(unitPrice).replace('\u00a0', ' ')} =  ${formatPrice(lineTotal).replace('\u00a0', ' ')}`;

export const formatVmkDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

/** Parse quote created_at / valid_until without shifting a DATE-only value by timezone. */
export const parseQuoteCoverDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const str = String(value).trim();
  if (!str) return null;
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Long Dutch date for cover page, e.g. "10 juni 2026" */
export const formatVmkCoverDate = (date = new Date()) => {
  const d = parseQuoteCoverDate(date) || new Date();
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
};
